import { Breadcrumb } from "react-bootstrap";
import { NavLink } from "react-router-dom";

import { Loader } from "../../../common/components/core";
import { InvoiceState } from "../../../types/invoice";
import { useContext, useEffect, useState } from "react";
import InvoiceContext from "../InvoiceContext";
import InvoiceFilesCard from "./InvoiceFilesCard";
import { shortenUUID } from "../../../utils/common";
import { PreAndPaymentStateWithDocuments } from "../../../types/payment";
import { BesuClaimState } from "../../../types/claim";
import { PageTitle } from "../../../types/pageTitle";
import { usePaymentOrderListById } from "../../../api/paymentOrder";
import { comparePaymentDateDesc } from "../../../utils/payment";
import PaymentOrderAndInvoiceDetailCard from "./PaymentOrderAndInvoiceDetailCard";
import { useClaim } from "../../../api/claim";
import { useDispatch } from "../../../redux/hooks";
import { getContactByAccountName } from "../../../redux/slices/accountInfo/thunks";
import styled from "styled-components";

const HSBCOperatorInvoiceDetailView = (): JSX.Element => {
    document.title = PageTitle.HSBCOperatorInvoiceDetailView;
    const {
        data: {
            state: { data: invoice },
        },
    } = useContext(InvoiceContext);
    const { data: claim, isValidating: isClaimValidating } = useClaim(
        invoice.props.claimLinearId.id,
    );
    const { data: preAndPayments, isValidating: isPOListValidating } =
        usePaymentOrderListById(invoice.linearId.id);

    return (
        <Loader isLoading={isPOListValidating && isClaimValidating}>
            {preAndPayments && claim && (
                <PureHSBCOperatorInvoiceDetailView
                    invoice={invoice}
                    claim={claim}
                    preAndPayments={preAndPayments}
                />
            )}
        </Loader>
    );
};

type PureHSBCOperatorInvoiceDetailViewProps = {
    invoice: InvoiceState;
    claim: BesuClaimState;
    preAndPayments: PreAndPaymentStateWithDocuments[];
};

export const PureHSBCOperatorInvoiceDetailView = ({
    invoice,
    claim,
    preAndPayments,
}: PureHSBCOperatorInvoiceDetailViewProps): JSX.Element => {
    const preAndPayment = [...preAndPayments].sort(comparePaymentDateDesc)[0];
    const paymentApprovers = preAndPayment.paymentOrderState.props.approvers;
    const [developerName, setDeveloperName] = useState<string>("");
    const dispatch = useDispatch();
    useEffect(() => {
        const getDeveloperInfo = async () => {
            const contactResponse = await dispatch(getContactByAccountName(claim.developer.accountName)).unwrap();
            setDeveloperName(contactResponse.name);
        };
        getDeveloperInfo();

        return () => {
            setDeveloperName("");
        };
    }, [invoice, claim.developer.accountName, dispatch]);
    return (
        <>
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/invoices">Invoice List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Invoice</Breadcrumb.Item>
            </Breadcrumb>
            <dl>
                <h1 className="flex-grow-1 mb-2">
                    Invoices shared by {developerName}
                </h1>
                <div className="mb-5">
                    <p className="mb-5">
                        Please note that the following invoices has been shared to
                        HSBC.
                    </p>
                    <div className="mb-5">
                        <dt><StyledLabel>Payment Order Approvers: </StyledLabel></dt>
                        {paymentApprovers.map((approver, index) => (
                            <dd key={index}>
                                <span>
                                    {`${approver.details.firstName} ${approver.details.lastName} (${approver.details.email})`}
                                    {index !== paymentApprovers.length - 1 && ", "}
                                </span>
                            </dd>
                        ))}
                    </div>
                    <StyledLabel>
                        <StyledDt>Claim ID:</StyledDt>
                        <StyledDd>
                            <span className="fw-normal">
                                {" "}
                                #{shortenUUID(claim.referenceId)}
                            </span>
                        </StyledDd>
                    </StyledLabel>
                    <StyledLabel>
                        <StyledDt>Invoice ID:</StyledDt>
                        <StyledDd>
                            <span className="fw-normal">
                                {" "}
                                #{shortenUUID(invoice.linearId.id)}
                            </span>
                        </StyledDd>
                    </StyledLabel>
                    <StyledLabel>
                        <StyledDt>Payment Order ID:</StyledDt>
                        <StyledDd>
                            <span className="fw-normal">
                                {" "}
                                #
                                {
                                    preAndPayment.paymentOrderState.props
                                        .customerRefNumber
                                }
                            </span>
                        </StyledDd>
                    </StyledLabel>
                </div>
            </dl>
            <InvoiceFilesCard
                invoice={invoice}
                developerAccountName={claim.developer?.accountName}
                contractorAccountName={claim.contractor?.accountName}
            />
            <PaymentOrderAndInvoiceDetailCard
                preAndPayment={preAndPayment}
                invoice={invoice}
            />
        </>
    );
};
const StyledLabel = styled.p`
    font-weight: 800;
    margin-bottom: 0;
`;
const StyledDt = styled.dt`
    font-weight: 800;
    display: inline-block;
`;
const StyledDd = styled.dd`
    font-weight: 800;
    display: inline-block;
`;

export default HSBCOperatorInvoiceDetailView;
